import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Button,
  Input, Text,
  VStack,
  List, ListItem,
  useDisclosure,
} from '@chakra-ui/react';

/** 件数リンク */
export const countLinkWidth = 80;

interface SelectorLinkProps {
  /** タイトル */
  title: string,
  // /** 件数 */
  // count: number,
  /** IDプロパティ名 */
  idName: string,
  /** 名称プロパティ名 */
  displayName: string,
  /** 一覧データ */
  listData: Record<string, any>[],
  /** 前回選択項目 */
  prevSelectedItem: Record<string, any> | null | undefined,
  /** 確定ボタン押下処理 */
  handleSetSelection: (selectedItem: Record<string, any> | null | undefined) => void,
}

/**
 * 一覧選択表示リンク
 */
const SelectorLink: React.FC<SelectorLinkProps> = ({
  title, idName, displayName, listData, prevSelectedItem, handleSetSelection,
}) => {
  const { t } = useTranslation();

  /** 状態管理 */
  const { isOpen, onOpen, onClose } = useDisclosure();

  /** フィルター値 */
  const [filterValue, setFilterValue] = useState("");

  /** 選択項目 */
  const [selectedItem, setSelectedItem] = useState<Record<string, any> | null>();

  /** フィルター後のリストデータ */
  const filteredList = (listData ?? []).filter((record) => (record[displayName] ?? '').toLowerCase().includes(filterValue.toLowerCase()));

  /** 件数リンクスタイル */
  const countLinkStyle = {
    textAlign: "right",
    paddingRight: "8px",
    display: "block",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: 'hidden',
    width: `${countLinkWidth}px`,
    textDecoration: "underline",
    color: "blue.500",
    cursor: "pointer"
  }

  /**
   * 前回選択値変更時に選択値を更新する。
   */
  useEffect(() => {
    setSelectedItem(prevSelectedItem);
  }, [prevSelectedItem]);

  return (
    <>
      {/* 選択ボタン */}
      <Text sx={countLinkStyle} onClick={onOpen}>
        {(listData?.length ?? 0) > 0 && (
          `${listData?.length ?? 0} ${t('common.label.count')}`
        )}
      </Text>
      {/* 非表示の間は出力しない */}
      {isOpen && (
        <Modal isOpen={isOpen} onClose={onClose} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{title}</ModalHeader>
            <ModalBody>
              <VStack spacing={4}>

                <Input placeholder={`${t('common.button.search')}...`} value={filterValue} onChange={(e) => setFilterValue(e.target.value)} />

                {/* 選択済み設置工場の表示 */}
                <Text fontWeight="bold" color="teal.500" alignSelf="flex-start">
                  {t('common.label.selected')}: {selectedItem?.[displayName] ?? listData?.find(m => m?.[idName] === selectedItem?.[idName])?.[displayName]}
                </Text>

                {/* 一覧表示 */}
                <List spacing={3} overflowY="auto" maxHeight="300px" w="100%">
                  {filteredList.map((record) => (
                    <ListItem
                      as="button"
                      display="block"
                      w="100%"
                      textAlign="left"
                      key={record[idName]}
                      p={2}
                      borderRadius="md"
                      bg={
                        selectedItem?.[idName] === record[idName]
                          ? "teal.100"
                          : "blackAlpha 100"
                      }
                      _focus={{ border: 'none 1px transparent' }}
                      _hover={{ bg: "blue.100" }}
                      onClick={() => setSelectedItem(record)}
                      cursor="pointer"
                    >
                      {record[displayName]}
                    </ListItem>
                  ))}
                </List>
              </VStack>

            </ModalBody>
            <ModalFooter>
              <Button colorScheme="blue" mr={1} ml={1}
                isDisabled={!selectedItem}
                onClick={() => {
                  handleSetSelection(selectedItem);
                  onClose();
                }}>
                {t('common.button.confirmAction')}
              </Button>
              <Button colorScheme="gray" mr={1} ml={1}
                onClick={onClose}>
                {t('common.button.close')}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </>
  );
}

export default SelectorLink;